import React from "react";
import { Box, Button, TextField, Typography, Stack, Divider, IconButton } from "@mui/material";
import {
  AddOutlined,
  DeleteOutlineOutlined,
  RemoveOutlined,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { t } from "i18next";
import axios from "axios";
import {
  getLocalStorageMenuItem,
  SARIconThemeColor,
  setMobileButttonBackColor,
  setMobileCardBorderColor,
  setMobileCardColor,
  setMobileTextColor,
} from "../../../utility";

const Order = ({
  order,
  currency,
  tableQr,
  tableName,
  refetch,
  handleOpenToast,
  ordersLength
}) => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const queryString = window.location.search;
  const urlParam = new URLSearchParams(queryString);
  const lang = getLocalStorageMenuItem("lang");
  // API mutation handlers
  const updateOrderField = async (field, values) => {
    const payload = {
      ...values,
      token: getLocalStorageMenuItem("gustToken"),
      menu_code: tableQr,
    };

    const { data: response } = await axios.post(
      `${process.env.REACT_APP_API}/basket-items/${order.index}`,
      payload
    );
    return response.data;
  };

  const { mutate: mutateOrderField, isLoading: isUpdatingOrder } = useMutation(
    (params) => updateOrderField(params.field, params.values),
    {
      onSuccess: () => refetch(),
      onError: (res) => {
        handleOpenToast(t(res.response.data.message), false);
      },
      onSettled: () => {
        queryClient.invalidateQueries("orderUpdates");
      },
    }
  );

  const deleteOrderItem = async () => {
    const { data: response } = await axios.delete(
      `${process.env.REACT_APP_API}/basket-items/${order.index}?token=${getLocalStorageMenuItem("gustToken")}&menu_code=${tableQr}`
    );
    return response.data;
  };

  const { mutate: deleteOrder, isLoading: isDeleting } = useMutation(
    deleteOrderItem,
    {
      onSuccess: () => {
        if (ordersLength === 1) {
          navigate(
            `/${tableName}/${tableQr}/sections?section=0&t=${urlParam.get("t")}&lang=${lang}`
          );
        } else {
          refetch();
        }


      },
      onError: (res) => {
        handleOpenToast(t(res.response.data.message), false);
      },
      onSettled: () => {
        queryClient.invalidateQueries("orderDeletion");
      },
    }
  );

  const renderCurrencyIcon = () => {
    if (!currency) return null;

    return currency === "SAR" ? (
      <img
        width={20}
        height={20}
        src={SARIconThemeColor()}
        className="mr-1"
        alt="SAR"
      />
    ) : (
      currency
    );
  };

  const renderPriceDisplay = (price, isChanged = false) => (
    <Typography
      variant="body1"
      className="cursor-pointer"
      color={isChanged ? "error" : setMobileTextColor()}
    >
      {price} <span className="text-sm">{renderCurrencyIcon()}</span>
    </Typography>
  );

  const renderQuantityControls = (item, isOption = false) => (
    <Box display="flex" alignItems="center">
      <Button
        onClick={() => {
          if (item.quantity >= 2) {
            mutateOrderField({
              field: "quantity",
              values: { quantity: item.quantity - 1, notes: "" },
            });
          }
        }}
        sx={{ height: 40, minWidth: 40 }}
        size="small"
        disabled={item.quantity <= 1}
      >
        <RemoveOutlined color="error" fontSize="small" />
      </Button>

      <TextField
        value={item.quantity}
        variant="outlined"
        size="small"
        sx={{
          width: 60,
          mx: 1,
          "& .MuiOutlinedInput-root": {
            color: setMobileTextColor(),
            backgroundColor: setMobileCardColor(),
            "& fieldset": {
              borderColor: setMobileCardBorderColor(),
            },
            "&.Mui-focused fieldset": {
              borderColor: setMobileButttonBackColor(),
            },
          },
        }}
        inputProps={{
          style: {
            textAlign: "center",
            padding: "8.5px 14px",
          },
        }}
      />

      <Button
        onClick={() => {
          if (item.quantity <= 100) {
            mutateOrderField({
              field: "quantity",
              values: { quantity: item.quantity + 1, notes: "" },
            });
          }
        }}
        sx={{ height: 40, minWidth: 40 }}
        size="small"
        disabled={item.quantity >= 100}
      >
        <AddOutlined
          color={
            getLocalStorageMenuItem("theme") === "costomize"
              ? "primary"
              : "inherit"
          }
          fontSize="small"
        />
      </Button>
    </Box>
  );

  const renderNotesField = (item) => (
    <TextField
      defaultValue={item.notes}
      onBlur={(e) =>
        mutateOrderField({
          field: "notes",
          values: { quantity: null, notes: e.target.value },
        })
      }
      placeholder={t("Enter your note")}
      variant="outlined"
      size="small"
      fullWidth
      sx={{
        mt: 1,
        "& .MuiOutlinedInput-root": {
          color: setMobileTextColor(),
          backgroundColor: setMobileCardColor(),
          "& fieldset": {
            borderColor: setMobileCardBorderColor(),
          },
          "&.Mui-focused fieldset": {
            borderColor: setMobileButttonBackColor(),
          },
        },
      }}
    />
  );

  const renderOrderOption = (option) => (
    <Box key={option.index} sx={{ mt: 1, position: 'relative' }}>
      {/* Left border indicator */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '2px',
        backgroundColor: 'divider'
      }} />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ color: setMobileTextColor() }}>
          {`${option.name} × ${option.quantity}`}
        </Typography>
        <Typography variant="body2" sx={{ color: setMobileTextColor() }}>
          {option.price} {renderCurrencyIcon()}
        </Typography>
      </Stack>
    </Box>
  );

  if (order.is_order_option && order?.is_combo_child) {
    return null;
  }

  return (
    <Box
      mt={2}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        border: `1px solid ${setMobileCardBorderColor()}`,
        borderRadius: 2,
        backgroundColor: setMobileCardColor(),
        padding: 2,
        gap: 2,
      }}
    >
      {/* === LEFT SIDE === */}
      <Box sx={{ flex: 1 }}>
        {/* Item Name + Edit */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: setMobileTextColor() }}>
          {order.name}
        </Typography>


        {/* Price */}
        <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600, color: setMobileTextColor() }}>
          {order.price} <span style={{ fontSize: 14 }}>{renderCurrencyIcon()}</span>
        </Typography>

        {/* Order Options */}
        {order.options && order.options.length > 0 && (
          <Box mt={1}>
            {order.options.map((option) => (
              <Typography
                key={option.index}
                variant="body2"
                sx={{ color: setMobileTextColor(), fontSize: 13 }}
              >
                • {option.name} × {option.quantity} – {option.price} {renderCurrencyIcon()}
              </Typography>
            ))}
          </Box>
        )}

        {/* Notes Input */}
        <TextField
          defaultValue={order.notes}
          onBlur={(e) =>
            mutateOrderField({
              field: "notes",
              values: { quantity: null, notes: e.target.value },
            })
          }
          placeholder={t("Enter your note")}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            mt: 1.5,
            "& .MuiOutlinedInput-root": {
              color: setMobileTextColor(),
              backgroundColor: setMobileCardColor(),
              "& fieldset": {
                borderColor: setMobileCardBorderColor(),
              },
              "&.Mui-focused fieldset": {
                borderColor: setMobileButttonBackColor(),
              },
            },
          }}
        />
      </Box>

      {/* === RIGHT SIDE (Image + Controls) === */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>

        {/* Product Image */}
        <Box
          component="img"
          src={order.image}
          alt={order.name}
          sx={{
            width: 100,
            height: 100,
            borderRadius: 2,
            objectFit: "cover",
            border: `1px solid ${setMobileCardBorderColor()}`,
          }}
        />
        {/* Quantity Controls ABOVE the image */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#fff",
            borderRadius: 999,
            boxShadow: 2,
            px: 1.5,
            py: 0.5,
            position: "relative",
            bottom: 10
          }}
        >
          <IconButton
            size="small"
            onClick={() => {
              if (order.quantity >= 2) {
                mutateOrderField({
                  field: "quantity",
                  values: { quantity: order.quantity - 1, notes: "" },
                });
              }
            }}
            sx={{ p: 0.5 }}
            disabled={order.quantity <= 1}
          >
            <RemoveOutlined fontSize="small" sx={{ color: "#f57c00" }} />
          </IconButton>

          <Typography sx={{ mx: 1, fontWeight: 600 }}>{order.quantity}</Typography>

          <IconButton
            size="small"
            onClick={() => {
              if (order.quantity <= 100) {
                mutateOrderField({
                  field: "quantity",
                  values: { quantity: order.quantity + 1, notes: "" },
                });
              }
            }}
            sx={{ p: 0.5 }}
          >
            <AddOutlined fontSize="small" sx={{ color: "#f57c00" }} />
          </IconButton>
        </Box>



        {/* Delete Icon BELOW the image */}
        <IconButton
          size="small"
          onClick={deleteOrder}
          sx={{
            mt: 1,
            p: 0.5,
            backgroundColor: "#fff",
            borderRadius: "50%",
            boxShadow: 2,
          }}
        >
          <DeleteOutlineOutlined fontSize="small" sx={{ color: "#f57c00" }} />
        </IconButton>
      </Box>
    </Box>
  );

};

export default Order;